import BRAND_CONFIG from './brandConfig.js';
import { formatPhoneNumber } from './phoneUtils.js';

const escapeHtml = (str = '') =>
  String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const fetchAsBase64 = async (url) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export const fetchSignatureAssets = async () => {
  const entries = await Promise.all([
    ['chainLogo', BRAND_CONFIG.assets.chainLogo],
    ['emailIcon', BRAND_CONFIG.assets.emailIcon],
    ['linkedinIcon', BRAND_CONFIG.assets.linkedinIcon],
    ['websiteIcon', BRAND_CONFIG.assets.websiteIcon],
    ...BRAND_CONFIG.partners.map((p) => [p.id, p.asset]),
  ].map(async ([key, path]) => [key, await fetchAsBase64(path)]));
  return Object.fromEntries(entries);
};

const renderPartnerBadge = (label, src) => {
  const safe = escapeHtml(label);
  return `<img src="${src}" alt="Workday ${safe}" width="64" height="64" style="display:block;border:0;outline:none;text-decoration:none;width:64px;height:64px;" />`;
};

const renderBadgesRow = (orderedPartners, assets) => {
  if (!orderedPartners.length) return '';
  const cells = orderedPartners
    .map((p, i) => {
      const isLast = i === orderedPartners.length - 1;
      const padding = isLast ? '' : 'padding-right:10px;';
      const src = (assets && assets[p.id]) || p.asset;
      return `<td style="${padding}vertical-align:top;">${renderPartnerBadge(p.label, src)}</td>`;
    })
    .join('');
  return `<tr><td colspan="2" style="padding-top:14px;"><table cellpadding="0" cellspacing="0" border="0"><tr>${cells}</tr></table></td></tr>`;
};

export const generateSignatureHTML = (data, assets = null) => {
  const { name, title, cell, main, email, linkedin, badges = [], tagline } = data;
  const c = BRAND_CONFIG.colors;
  const websiteText = BRAND_CONFIG.company.website;
  const websiteUrl = BRAND_CONFIG.company.websiteUrl;

  const chainLogoSrc = (assets && assets.chainLogo) || BRAND_CONFIG.assets.chainLogo;
  const emailIconSrc = (assets && assets.emailIcon) || BRAND_CONFIG.assets.emailIcon;
  const linkedinIconSrc = (assets && assets.linkedinIcon) || BRAND_CONFIG.assets.linkedinIcon;
  const websiteIconSrc = (assets && assets.websiteIcon) || BRAND_CONFIG.assets.websiteIcon;

  // cell:/main: in a single <td> instead of two columns. Two-column layout
  // makes browsers/email clients auto-distribute column widths within the
  // surrounding table (other rows use colspan=2 for tagline/badges, which
  // pushes the table much wider than the labels need), creating an
  // unwanted gap between the label and the phone number.
  const cellRow = cell
    ? `<tr><td colspan="2" style="font-family:Arial,Helvetica,sans-serif;font-size:14px;padding:0 0 2px 0;white-space:nowrap;vertical-align:top;"><span style="color:${c.orange};font-weight:700;">cell:</span>&nbsp;<span style="color:${c.text};">${escapeHtml(formatPhoneNumber(cell))}</span></td></tr>`
    : '';

  const mainRow = main
    ? `<tr><td colspan="2" style="font-family:Arial,Helvetica,sans-serif;font-size:14px;padding:0 0 2px 0;white-space:nowrap;vertical-align:top;"><span style="color:${c.orange};font-weight:700;">main:</span>&nbsp;<span style="color:${c.text};">${escapeHtml(formatPhoneNumber(main))}</span></td></tr>`
    : '';

  const websiteRow = `<tr><td colspan="2" style="padding:2px 0 0 0;"><table cellpadding="0" cellspacing="0" border="0"><tr><td style="padding:0 8px 0 0;vertical-align:middle;line-height:0;"><img src="${websiteIconSrc}" alt="" width="16" height="16" style="display:block;border:0;outline:none;" /></td><td style="vertical-align:middle;"><a href="${websiteUrl}" style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:${c.link};text-decoration:underline;">${websiteText}</a></td></tr></table></td></tr>`;

  const emailRow = email
    ? `<tr><td colspan="2" style="padding:2px 0 0 0;"><table cellpadding="0" cellspacing="0" border="0"><tr><td style="padding:0 8px 0 0;vertical-align:middle;line-height:0;"><img src="${emailIconSrc}" alt="" width="16" height="16" style="display:block;border:0;outline:none;" /></td><td style="vertical-align:middle;"><a href="mailto:${escapeHtml(email)}" style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:${c.link};text-decoration:underline;">${escapeHtml(email)}</a></td></tr></table></td></tr>`
    : '';

  const linkedinRow = linkedin
    ? `<tr><td colspan="2" style="padding:2px 0;"><table cellpadding="0" cellspacing="0" border="0"><tr><td style="padding:0 8px 0 0;vertical-align:middle;line-height:0;"><img src="${linkedinIconSrc}" alt="" width="16" height="16" style="display:block;border:0;outline:none;" /></td><td style="vertical-align:middle;"><a href="${escapeHtml(linkedin)}" style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:${c.link};text-decoration:underline;">LinkedIn Profile</a></td></tr></table></td></tr>`
    : '';

  const taglineRow = tagline
    ? `<tr><td colspan="2" style="padding:8px 0 0 0;"><span style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:${c.orange};font-style:italic;mso-style-textfill-fill-color:${c.orange};">${escapeHtml(tagline)}</span></td></tr>`
    : '';

  const orderedPartners = BRAND_CONFIG.partners.filter((p) => badges.includes(p.id));
  const badgesHtml = renderBadgesRow(orderedPartners, assets);

  // Outer wrapper has a fixed width and explicit white background. Outlook
  // desktop in dark mode strips bgcolor on standard tables, so we wrap the
  // whole thing in an MSO-only VML <v:rect fillcolor="#FFFFFF"> — Outlook
  // honors VML fill colors even in dark mode. Non-Outlook clients ignore
  // the conditional comments and render the inner table normally.
  return `<!--[if mso]>
<v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" arcsize="0%" fillcolor="#FFFFFF" stroke="f" style="width:450pt;mso-fit-shape-to-text:true;">
<v:fill type="solid" color="#FFFFFF" />
<v:textbox inset="0,0,0,0" style="mso-fit-shape-to-text:true;">
<![endif]-->
<table bgcolor="#FFFFFF" cellpadding="0" cellspacing="0" border="0" width="600" style="background-color:#FFFFFF !important;border-collapse:collapse;width:600px;max-width:600px;mso-line-height-rule:exactly;">
  <tr>
    <td bgcolor="#FFFFFF" style="background-color:#FFFFFF !important;padding:20px;mso-line-height-rule:exactly;">
      <table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-family:Arial,Helvetica,sans-serif;color:${c.text};">
        <tr>
          <td valign="top" style="padding:0 22px 0 0;width:78px;">
            <img src="${chainLogoSrc}" alt="3LINK Solutions" width="62" height="133" style="display:block;border:0;outline:none;text-decoration:none;" />
          </td>
          <td valign="top">
            <table cellpadding="0" cellspacing="0" border="0">
              <tr>
                <td colspan="2" style="padding:0 0 4px 0;">
                  <span style="font-family:Arial,Helvetica,sans-serif;font-size:24px;font-weight:700;color:${c.text};letter-spacing:-0.3px;">${escapeHtml(name) || '[Your Name]'}</span>
                </td>
              </tr>
              <tr>
                <td colspan="2" style="padding:0 0 12px 0;">
                  <span style="font-family:Arial,Helvetica,sans-serif;font-size:15px;color:${c.gray};">${escapeHtml(title) || '[Your Job Title]'}</span>
                </td>
              </tr>
              ${cellRow}
              ${mainRow}
              ${websiteRow}
              ${emailRow}
              ${linkedinRow}
              ${taglineRow}
              ${badgesHtml}
            </table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
<!--[if mso]>
</v:textbox>
</v:roundrect>
<![endif]-->`;
};

// Render the signature element at 2× for crisp anti-aliased text. We do
// NOT downsample — earlier attempts to cap the output width produced soft
// images. Native canvas size (~2× the preview's CSS width) gives Outlook a
// crisp PNG that the user can resize in compose if it's too big.
const CAPTURE_SCALE = 2;
const TRIM_PADDING_DISPLAY_PX = 24;
const MIN_TRIMMED_DISPLAY_WIDTH = 540;
const NEAR_WHITE_THRESHOLD = 250;

const renderElementToCanvas = async (element) => {
  const { default: html2canvas } = await import('html2canvas');
  return html2canvas(element, {
    backgroundColor: '#ffffff',
    scale: CAPTURE_SCALE,
    useCORS: true,
    allowTaint: false,
    logging: false,
    imageTimeout: 15000,
  });
};

// Crop trailing whitespace off the captured canvas. The signature's outer
// table has a fixed width which leaves empty white columns on the right
// when the actual content is narrower. Scanning column-by-column from the
// right finds the rightmost non-white pixel and crops to that + a small
// padding, so the exported image is content-tight regardless of source
// width.
const trimRightWhitespace = (canvas) => {
  const w = canvas.width;
  const h = canvas.height;
  const ctx = canvas.getContext('2d');
  const data = ctx.getImageData(0, 0, w, h).data;

  let rightBound = 0;
  for (let x = w - 1; x >= 0; x--) {
    let hasContent = false;
    for (let y = 0; y < h; y++) {
      const idx = (y * w + x) * 4;
      if (
        data[idx] < NEAR_WHITE_THRESHOLD ||
        data[idx + 1] < NEAR_WHITE_THRESHOLD ||
        data[idx + 2] < NEAR_WHITE_THRESHOLD
      ) {
        hasContent = true;
        break;
      }
    }
    if (hasContent) {
      rightBound = x;
      break;
    }
  }

  const padding = TRIM_PADDING_DISPLAY_PX * CAPTURE_SCALE;
  const minWidth = MIN_TRIMMED_DISPLAY_WIDTH * CAPTURE_SCALE;
  const newWidth = Math.min(w, Math.max(rightBound + padding, minWidth));
  if (newWidth >= w) return canvas;

  const trimmed = document.createElement('canvas');
  trimmed.width = newWidth;
  trimmed.height = h;
  const tctx = trimmed.getContext('2d');
  tctx.fillStyle = '#FFFFFF';
  tctx.fillRect(0, 0, newWidth, h);
  tctx.drawImage(canvas, 0, 0);
  return trimmed;
};

const renderTrimmedCanvas = async (element) => {
  const captured = await renderElementToCanvas(element);
  return trimRightWhitespace(captured);
};

const canvasToBlob = (canvas) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Failed to generate image blob'));
    }, 'image/png', 1.0);
  });

export const downloadSignatureAsImage = async (element, filename) => {
  const canvas = await renderTrimmedCanvas(element);
  const dataUrl = canvas.toDataURL('image/png', 1.0);
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const copySignatureAsImage = async (element) => {
  const canvas = await renderTrimmedCanvas(element);
  const blob = await canvasToBlob(canvas);
  if (!navigator.clipboard || !window.ClipboardItem) {
    throw new Error('Clipboard image API not supported in this browser');
  }
  await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
  return true;
};

// Image-as-HTML: render the signature, embed it as a base64 <img> wrapped
// in an <a> click-through. The white background is baked into the image,
// which Outlook desktop dark mode can't strip. Trade-off: only the wrapper
// link is clickable; email/phone/LinkedIn icons inside the image are not.
// Wrapper points to the user's email (mailto:) when provided so replying
// is one click away. Falls back to the website when no email is supplied.
export const copySignatureAsHtmlImage = async (element, { email } = {}) => {
  const canvas = await renderTrimmedCanvas(element);
  const dataUrl = canvas.toDataURL('image/png', 1.0);
  // Display width = canvas width / 2 (we captured at 2×). Setting an
  // explicit width on the <img> lets it shrink when pasted, while the
  // intrinsic pixel data stays high-res.
  const displayWidth = Math.round(canvas.width / CAPTURE_SCALE);
  const wrapperHref = email ? `mailto:${email}` : BRAND_CONFIG.company.websiteUrl;
  const html = `<a href="${escapeHtml(wrapperHref)}" style="display:inline-block;border:0;outline:none;text-decoration:none;"><img src="${dataUrl}" alt="${escapeHtml(BRAND_CONFIG.company.name)} email signature" width="${displayWidth}" style="display:block;border:0;outline:none;text-decoration:none;" /></a>`;
  return copySignatureToClipboard(html);
};

export const copySignatureToClipboard = async (html) => {
  if (navigator.clipboard && window.ClipboardItem) {
    try {
      const item = new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([html], { type: 'text/plain' }),
      });
      await navigator.clipboard.write([item]);
      return true;
    } catch (e) {
      // fall through to fallback
    }
  }

  try {
    const tmp = document.createElement('div');
    tmp.contentEditable = 'true';
    tmp.innerHTML = html;
    tmp.style.position = 'fixed';
    tmp.style.left = '-9999px';
    document.body.appendChild(tmp);
    const range = document.createRange();
    range.selectNodeContents(tmp);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    const ok = document.execCommand('copy');
    sel.removeAllRanges();
    document.body.removeChild(tmp);
    if (ok) return true;
  } catch (e) {
    // fall through
  }

  await navigator.clipboard.writeText(html);
  return true;
};
